import { useState, useEffect } from 'react';
import { api } from './_barrel.js';


        /* ============ Live ticker — scrolls recent hostile headlines + collector status ============ */
        function LiveTicker({ token }) {
            const [headlines, setHeadlines] = useState([]);
            const [collectors, setCollectors] = useState([]);
            useEffect(() => {
                let cancelled = false;
                const load = async () => {
                    try {
                        // Two feeds in parallel:
                        //   - requires-attention: the hostile mentions and incidents the analyst should see
                        //   - source-health:     last collector activity, used as a fallback when no mentions
                        const [att, sh] = await Promise.all([
                            api('/api/v1/dashboard/requires-attention', {}, token).catch(() => ({items:[]})),
                            api('/api/v1/source-health', {}, token).catch(() => ({collectors:[]})),
                        ]);
                        if (cancelled) return;

                        // Hostile headlines — anything in attention feed counts
                        const heads = (att.items || []).slice(0, 12).map(it => ({
                            time: it.seen_at ? new Date(it.seen_at) : new Date(),
                            severity: it.severity_band || 'info',
                            target: it.target_name || '',
                            target_kind: (it.target_kind || '').toUpperCase(),
                            title: it.title || it.subtitle || '(no headline)',
                            classification: it.classification || it.kind || '',
                        }));
                        setHeadlines(heads);

                        // Collector status — used as filler when there are no headlines
                        const colEvents = (sh.collectors || [])
                            .filter(c => c.last_run_at)
                            .slice(0, 6)
                            .map(c => ({
                                time: new Date(c.last_run_at),
                                label: c.label,
                                summary: c.last_result_summary || (c.on_demand ? 'on-demand' : 'completed'),
                            }));
                        setCollectors(colEvents);
                    } catch (e) {
                        // Silently fail — ticker is decorative
                    }
                };
                load();
                const id = setInterval(load, 60000);
                return () => { cancelled = true; clearInterval(id); };
            }, [token]);

            const now = new Date();
            const dateStr = now.toLocaleString('en-GB', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC'
            }).toUpperCase() + ' UTC';
            const fmtTime = (d) => d.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit' });

            // Severity → CSS class for colouring inside the ticker
            const sevTone = (s) => {
                if (s === 'critical') return 't-red';
                if (s === 'high')     return 't-amber';
                if (s === 'moderate') return 't-name';   // gold-ish
                return '';
            };

            // Build the ticker — headlines first (always shown if present), collectors below
            const renderRun = () => (
                <>
                    <span className="t-time">{dateStr}</span>
                    {headlines.length > 0
                        ? <span className="t-cyan">◉ ACTIVE INTEL FEED</span>
                        : <span className="t-cyan">◉ SYSTEM LIVE</span>
                    }
                    {headlines.map((h, idx) => (
                        <span key={`h-${idx}`}>
                            <span className="t-time">{fmtTime(h.time)}</span> ·{' '}
                            <span className={sevTone(h.severity)}>{h.severity.toUpperCase()}</span> ·{' '}
                            <span className="t-name">{h.target}</span>
                            {h.target_kind && <> <span className="t-time">({h.target_kind})</span></>} ·{' '}
                            {h.title}
                        </span>
                    ))}
                    {collectors.map((c, idx) => (
                        <span key={`c-${idx}`}>
                            <span className="t-time">{fmtTime(c.time)}</span> ·{' '}
                            <span className="t-name">{c.label}</span> · {c.summary}
                        </span>
                    ))}
                    {headlines.length === 0 && collectors.length === 0 && (
                        <>
                            <span>AWAITING ACTIVITY</span>
                            <span className="t-time">FEED REFRESHES EVERY 60S</span>
                        </>
                    )}
                </>
            );

            // The marquee needs a second copy of the run to scroll seamlessly —
            // but only when there's actually something scrolling. Idle, a single
            // calm status line reads as intentional, not a doubled-up glitch.
            const hasFeed = headlines.length > 0 || collectors.length > 0;
            return (
                <div className="ticker">
                    <div className="ticker-label">◉ LIVE</div>
                    <div className="ticker-content" style={hasFeed ? undefined : { animation: 'none', whiteSpace: 'normal', flexWrap: 'wrap', gap: '4px 16px' }}>
                        {renderRun()}
                        {hasFeed && renderRun()}
                    </div>
                </div>
            );
        }

export { LiveTicker };
